import { useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { ProfessionalColumns } from './components/ProfessionalColumns';
import { ProfessionalAdminFilters } from './components/ProfessionalAdminFilters';
import { ProfessionalAuditModal } from './components/ProfessionalAuditModal';
import { ProfessionalDisplayOrderModal } from './components/ProfessionalDisplayOrderModal';
import { Button } from '@/components/ui';
import { ArrowUpDown } from 'lucide-react';
import { useGetProfessionals, useGetServices } from '@/services';
import { SearchInput, DataTable, PageTitle } from '@/components';
import { getBackendErrorMessage } from '@/utils/errorMessage';
import { parsePermissionTagsFromSearchParams } from '@/utils/professionalPermissionTags';

const Professionals = () => {
	const [searchParams] = useSearchParams();
	const [page, setPage] = useState(Number(searchParams.get('page')) || 1);
	const [limit, setLimit] = useState(10);
	const [auditProfessional, setAuditProfessional] =
		useState<Professionals.Professional | null>(null);
	const [isDisplayOrderOpen, setIsDisplayOrderOpen] = useState(false);
	const navigate = useNavigate();

	const authorizedParams = searchParams.get('authorized') as 'true' | 'false';
	const contractTypeParams = searchParams.get(
		'contract',
	) as Professionals.Contract;
	const serviceModalityParams = searchParams.get(
		'service_modality',
	) as Professionals.ServiceModality;
	const serviceIdParams = searchParams.get('service_id') || undefined;
	const search = searchParams.get('search') as string;
	const permissionTagsParams = useMemo(
		() => parsePermissionTagsFromSearchParams(searchParams),
		[searchParams],
	);

	const { data: servicesData } = useGetServices();
	const services = (servicesData ?? []).filter(
		(service) => service.type === 'PROFESSION',
	);

	const permissionTagsKey = permissionTagsParams.join(',');

	useEffect(() => {
		setPage(1);
	}, [
		authorizedParams,
		contractTypeParams,
		serviceModalityParams,
		serviceIdParams,
		search,
		permissionTagsKey,
	]);

	const handlePageChange = (newPage: number) => setPage(newPage);

	const handleLimitChange = (newLimit: number) => {
		setLimit(newLimit);
		setPage(1);
	};

	const handleEdit = (id: string) =>
		navigate(`/admin/profissionais/editar/${id}`);

	const handleEditPatients = (id: string) =>
		navigate(`/admin/profissionais/editar/${id}/pacientes`);

	const handleViewUsage = (id: string) =>
		navigate(`/admin/profissionais/editar/${id}/uso`);

	const handleViewAudit = (professional: Professionals.Professional) =>
		setAuditProfessional(professional);

	const { data, error, isError } = useGetProfessionals({
		page,
		limit,
		search,
		service_modality: serviceModalityParams,
		service_id: serviceIdParams,
		contract: contractTypeParams,
		authorized: authorizedParams,
		permission_tags:
			permissionTagsParams.length > 0 ? permissionTagsParams : undefined,
	});

	const searchErrorMessage =
		search?.trim() && isError ? getBackendErrorMessage(error) : null;

	return (
		<section className="animate-fade-in space-y-6 px-4 py-6">
			<div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
				<div className="space-y-1">
					<PageTitle text="Profissionais" />
					<p className="text-sm text-stone-400">
						Gerencie os profissionais e suas autorizações.
					</p>
				</div>
				<Button
					text="Ordenar exibição"
					title="Ordem em que os profissionais visíveis aparecem na home e no agendamento do usuário"
					className="w-full rounded-full bg-black px-6 py-2.5 font-bold shadow-md hover:bg-neutral-800 sm:w-auto"
					startIcon={
						<ArrowUpDown className="size-5 stroke-[3] text-white" />
					}
					onClick={() => setIsDisplayOrderOpen(true)}
				/>
			</div>
			<div className="space-y-2">
				<SearchInput
					placeholder="Pesquisar por nome..."
					className="max-w-full bg-inherit md:max-w-full"
				/>
				{searchErrorMessage && (
					<p className="text-sm text-red-600" role="alert">
						{searchErrorMessage}
					</p>
				)}
			</div>
			<ProfessionalAdminFilters services={services} />
			{data && (
				<DataTable
					tableClassName="table-fixed w-full"
					columns={ProfessionalColumns({
						onEdit: handleEdit,
						onEditPatients: handleEditPatients,
						onViewUsage: handleViewUsage,
						onViewAudit: handleViewAudit,
					})}
					data={data?.data ?? data?.professionals ?? []}
					page={page}
					limit={limit}
					totalItems={data.total || 0}
					onPageChange={handlePageChange}
					onLimitChange={handleLimitChange}
				/>
			)}
			<ProfessionalDisplayOrderModal
				isOpen={isDisplayOrderOpen}
				onClose={() => setIsDisplayOrderOpen(false)}
			/>
			<ProfessionalAuditModal
				isOpen={auditProfessional != null}
				onClose={() => setAuditProfessional(null)}
				professional={auditProfessional}
			/>
		</section>
	);
};

export { Professionals };
